import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { AGE_LINE, ageFrom, consentMetadata } from '../lib/consent';
import { ConsentBoxes, consentMissing } from '../shared/ConsentBoxes';
import { signupBirthMetadata } from '../lib/signupBirth';
import { BackArrow } from '../shared/BackArrow';

// Dark, to match the rest of the app.
const PD = {
  bg:        "#0C0E1A",
  bgCard:    "#1C1E34",
  text:      "#E4DED4",
  sub:       "#C9C2B8",
  dim:       "#A8A098",
  faint:     "#A8A098",
  border:    "#383650",
  accent:    "#9AA6D6",
};

const B = { fontFamily: "'Geist', 'Helvetica Neue', sans-serif" };

function Logo({ size = 48 }) {
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} fill="none">
      <circle cx="14" cy="16" r="2.2" fill="#4D8A8A" opacity="0.95" />
      <circle cx="34" cy="12" r="1.8" fill="#5E6AAE" opacity="0.9" />
      <circle cx="24" cy="28" r="2.5" fill="#8B7BC2" opacity="0.95" />
      <circle cx="38" cy="32" r="1.6" fill="#B06B7D" opacity="0.85" />
      <circle cx="10" cy="36" r="2" fill="#5E8A6A" opacity="0.9" />
      <line x1="14" y1="16" x2="34" y2="12" stroke="#9995A8" strokeWidth="0.4" opacity="0.55" />
      <line x1="14" y1="16" x2="24" y2="28" stroke="#9995A8" strokeWidth="0.4" opacity="0.55" />
      <line x1="34" y1="12" x2="24" y2="28" stroke="#9995A8" strokeWidth="0.4" opacity="0.55" />
      <line x1="24" y1="28" x2="38" y2="32" stroke="#9995A8" strokeWidth="0.4" opacity="0.55" />
      <line x1="24" y1="28" x2="10" y2="36" stroke="#9995A8" strokeWidth="0.4" opacity="0.55" />
    </svg>
  );
}

function friendlyError(err) {
  const m = (err?.message || "").toLowerCase();
  if (m.includes("already registered") || m.includes("already exists")) return "That email already has an account. Sign in instead.";
  if (m.includes("invalid login") || m.includes("invalid credentials")) return "That email and password don't match. Try again or reset your password.";
  if (m.includes("password") && m.includes("6")) return "Use a longer password (8 or more characters).";
  if (m.includes("rate") || m.includes("too many")) return "Too many tries. Wait a minute and try again.";
  // signup_refused: any 5xx on sign-up, which includes the server's consent check (ba-012).
  if (m === "signup_refused") return "We couldn't make your account just now. Reload the page and try again.";
  if (m.includes("network") || m.includes("fetch")) return "Couldn't reach the server. Check your connection and try again.";
  return err?.message || "Something went wrong. Try again.";
}

export default function Auth({ onAuthComplete, defaultMode = 'signup', onBack, birthDate, birthData, onEditBirth }) {
  const [mode, setMode] = useState(defaultMode); // 'signup' | 'signin' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [confirmSent, setConfirmSent] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [ageOk, setAgeOk] = useState(false);
  const [consentOk, setConsentOk] = useState(false);
  // The first run's birth date already says how old they are.
  const age = ageFrom(birthDate);
  const tooYoung = mode === 'signup' && age !== null && age < AGE_LINE;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // No account without both boxes.
    if (mode === 'signup' && (tooYoung || !ageOk || !consentOk)) {
      setError(consentMissing(ageOk, consentOk));
      return;
    }
    setLoading(true);

    try {
      if (mode === 'forgot') {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin,
        });
        if (resetError) throw resetError;
        setForgotSent(true);
        setLoading(false);
        return;
      }

      if (mode === 'signup') {
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          // The birth details ride along, so confirming the email in another
          // browser (one without this guest chart) still saves the chart.
          options: { data: { ...consentMetadata(), ...signupBirthMetadata(birthData) } },
        });
        if (signUpError) {
          // A server refusal (ba-012's consent check) arrives as a 500.
          if (signUpError.status >= 500) throw new Error("signup_refused");
          throw signUpError;
        }
        // If the project requires email confirmation there's no session yet.
        if (!data?.session) {
          setConfirmSent(true);
          setLoading(false);
          return;
        }
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (signInError) throw signInError;
      }

      if (onAuthComplete) onAuthComplete();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  if (confirmSent) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "56px 24px", background: PD.bg }}>
        <Logo size={52} />
        <h2 style={{ ...B, fontSize: 22, fontWeight: 500, color: PD.text, marginTop: 28, marginBottom: 12 }}>Check your email</h2>
        <p style={{ ...B, fontSize: 14, color: PD.sub, textAlign: "center", maxWidth: 340, lineHeight: 1.6 }}>
          We sent a link to <strong>{email}</strong>. Open it in any browser to finish saving your chart.
        </p>
        {onBack && (
          <button onClick={onBack} style={{ ...B, fontSize: 14, color: PD.sub, background: "none", border: `1px solid ${PD.border}`, padding: "10px 22px", borderRadius: 999, cursor: "pointer", marginTop: 24, minHeight: 44 }}>
            Back to my chart
          </button>
        )}
      </div>
    );
  }

  if (forgotSent) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "56px 24px",
        background: PD.bg,
      }}>
        <Logo size={52} />
        <h2 style={{ ...B, fontSize: 22, fontWeight: 500, color: PD.text, marginTop: 28, marginBottom: 12 }}>
          Check your email
        </h2>
        <p style={{ ...B, fontSize: 14, color: PD.sub, textAlign: "center", maxWidth: 340, lineHeight: 1.6 }}>
          We sent a password reset link to <strong>{email}</strong>. Open it to choose a new password.
        </p>
        <button
          onClick={() => { setForgotSent(false); setMode('signin'); }}
          style={{
            ...B, marginTop: 24, background: "none", border: "none",
            color: PD.accent, fontSize: 13, cursor: "pointer",
            padding: "10px 12px", minHeight: 44,
          }}
        >
          Back to sign in
        </button>
      </div>
    );
  }

  if (tooYoung) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "56px 24px", background: PD.bg }}>
        <Logo size={52} />
        <h2 style={{ ...B, fontSize: 22, fontWeight: 500, color: PD.text, marginTop: 28, marginBottom: 12 }}>Accounts start at {AGE_LINE}</h2>
        <p style={{ ...B, fontSize: 14, color: PD.sub, textAlign: "center", maxWidth: 340, lineHeight: 1.6 }}>
          Your birth date puts you under {AGE_LINE}, so accounts aren't open to you yet. Your chart stays on this device, and it's all still here to read.
        </p>
        {onBack && (
          <button onClick={onBack} style={{ ...B, fontSize: 14, color: PD.sub, background: "none", border: `1px solid ${PD.border}`, padding: "10px 22px", borderRadius: 999, cursor: "pointer", marginTop: 24, minHeight: 44 }}>
            Back to my chart
          </button>
        )}
        {onEditBirth && (
          <button onClick={onEditBirth} style={{ ...B, fontSize: 13, color: PD.accent, background: "none", border: "none", padding: "10px 12px", cursor: "pointer", marginTop: 8, minHeight: 44 }}>
            Wrong birth date? Fix it
          </button>
        )}
        <button onClick={() => { setMode('signin'); setError(''); }} style={{ ...B, fontSize: 12, color: PD.sub, background: "none", border: "none", padding: "4px 12px", cursor: "pointer", marginTop: onEditBirth ? 0 : 8, minHeight: 44 }}>
          I already have an account
        </button>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      padding: "56px 24px",
      background: PD.bg,
      position: "relative",
    }}>
      {onBack && (
        <div style={{ position: "absolute", top: 8, left: 20 }}>
          <BackArrow onClick={onBack} color={PD.sub} />
        </div>
      )}
      <Logo size={52} />

      <h2 style={{
        ...B, fontSize: 22, fontWeight: 500, color: PD.text,
        marginTop: 28, marginBottom: mode === 'forgot' ? 6 : 32,
      }}>
        {mode === 'signup' ? 'Save your chart' : mode === 'forgot' ? 'Reset your password' : 'Welcome back'}
      </h2>
      {mode === 'forgot' && (
        <p style={{ ...B, fontSize: 14, color: PD.sub, marginBottom: 32, textAlign: "center", maxWidth: 340, lineHeight: 1.55 }}>
          We'll email you a link to choose a new one.
        </p>
      )}

      <form onSubmit={handleSubmit} style={{ width: "100%", maxWidth: 380 }}>
        {/* Email */}
        <div style={{ marginBottom: 20 }}>
          <label htmlFor="auth-email" style={{ ...B, fontSize: 13, color: PD.sub, display: "block", marginBottom: 6 }}>
            Email
          </label>
          <input
            id="auth-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            autoComplete="email"
            required
            style={{
              width: "100%", ...B, fontSize: 16, colorScheme: "dark", minHeight: 50,
              padding: "12px 16px", background: PD.bgCard,
              border: `1px solid ${PD.border}`, color: PD.text,
              outline: "none", borderRadius: 8,
              transition: "border-color 0.2s",
            }}
            onFocus={(e) => (e.target.style.borderColor = PD.accent)}
            onBlur={(e) => (e.target.style.borderColor = PD.border)}
          />
        </div>

        {/* Password (hidden in forgot mode) */}
        {mode !== 'forgot' && (
          <div style={{ marginBottom: 8 }}>
            <label htmlFor="auth-password" style={{ ...B, fontSize: 13, color: PD.sub, display: "block", marginBottom: 6 }}>
              Password
            </label>
            <div style={{ position: "relative" }}>
            <input
              id="auth-password"
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === 'signup' ? '8+ characters' : 'Your password'}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              required
              minLength={8}
              style={{
                width: "100%", ...B, fontSize: 16, colorScheme: "dark", minHeight: 50,
                padding: "12px 64px 12px 16px", background: PD.bgCard,
                border: `1px solid ${PD.border}`, color: PD.text,
                outline: "none", borderRadius: 8,
                transition: "border-color 0.2s",
              }}
              onFocus={(e) => (e.target.style.borderColor = PD.accent)}
              onBlur={(e) => (e.target.style.borderColor = PD.border)}
            />
            <button type="button" onClick={() => setShowPw(!showPw)} aria-label={showPw ? "Hide password" : "Show password"} style={{
              ...B, position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", background: "none", border: "none",
              color: PD.accent, fontSize: 13, cursor: "pointer", padding: "10px 10px", minHeight: 44,
            }}>{showPw ? "Hide" : "Show"}</button>
            </div>
          </div>
        )}

        {/* Forgot password link */}
        {mode === 'signin' && (
          <div style={{ textAlign: "right", marginBottom: 20 }}>
            <button
              type="button"
              onClick={() => { setMode('forgot'); setError(''); }}
              style={{
                ...B, background: "none", border: "none",
                color: PD.sub, fontSize: 13, cursor: "pointer",
                padding: "10px 8px", minHeight: 44,
              }}
            >
              Forgot password?
            </button>
          </div>
        )}
        {mode === 'signup' && (
          <div style={{ marginTop: 12, marginBottom: 16 }}>
            <ConsentBoxes
              ageOk={ageOk} onAge={(v) => { setAgeOk(v); setError(''); }}
              consentOk={consentOk} onConsent={(v) => { setConsentOk(v); setError(''); }}
            />
          </div>
        )}

        {/* Error */}
        {error && (
          <p role="alert" style={{
            ...B, fontSize: 13, color: "#E87070", marginBottom: 16,
            padding: "10px 14px", background: "#E8707015", borderRadius: 8,
            border: `1px solid #E8707030`,
          }}>
            {error}
          </p>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          style={{
            width: "100%", ...B, fontSize: 14, fontWeight: 600,
            letterSpacing: "0.02em",
            backgroundColor: loading ? PD.border : "#A9B4E4",
            color: PD.bg, border: "none", minHeight: 52, fontSize: 15,
            padding: "14px 24px", cursor: loading ? "default" : "pointer",
            borderRadius: 32, transition: "all 0.3s ease",
          }}
        >
          {loading
            ? 'One moment...'
            : mode === 'signup'
            ? 'Create account'
            : mode === 'forgot'
            ? 'Send reset link'
            : 'Sign in'}
        </button>
      </form>

      {/* Toggle mode */}
      <p style={{ ...B, fontSize: 13, color: PD.sub, marginTop: 24 }}>
        {mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}
        {' '}
        <button
          onClick={() => {
            setMode(mode === 'signup' ? 'signin' : 'signup');
            setError('');
          }}
          style={{
            ...B, background: "none", border: "none",
            color: PD.accent, fontSize: 13, cursor: "pointer",
            fontWeight: 500, padding: "10px 6px", minHeight: 44,
          }}
        >
          {mode === 'signup' ? 'Sign in' : 'Create one'}
        </button>
      </p>
    </div>
  );
}
